import React, { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Timestamp } from "firebase/firestore";
import { toast } from "react-hot-toast";
import { ConversationList } from "./ConversationList";
import { MessageThread } from "./MessageThread";

interface Conversation {
  userId: string;
  phone: string;
  userName: string;
  lastMessage: string;
  lastMessageTime: Timestamp | Date | string | null;
  unreadCount: number;
}

interface NormalizedMessage {
  id: string;
  content: string;
  text?: { body: string };
  message?: string;
  type?: string;
  from?: string;
  direction: "inbound" | "outbound";
  timestamp: Date | null;
  status?: string;
}

export const WhatsAppDashboard: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [messages, setMessages] = useState<NormalizedMessage[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const functions = getFunctions();
	const selectedConv = selectedUserId
  ? conversations.find((c) => c.userId === selectedUserId)
  : null;

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const getConversations = httpsCallable(functions, "getConversations");
      const result = await getConversations({ limit: 50 });
      const data = result.data as any;

      if (data.success) {
        const sanitized = data.conversations.map(
          (conv: any): Conversation => ({
            userId: conv.userId,
            phone: conv.phone,
            userName: conv.userName,
            lastMessage:
              typeof conv.lastMessage === "string" ? conv.lastMessage : "",
            lastMessageTime: conv.lastMessageTime?.toDate
              ? conv.lastMessageTime.toDate()
              : conv.lastMessageTime,
            unreadCount: conv.unreadCount || 0,
          })
        );
        setConversations(sanitized);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
      toast.error("Failed to load conversations");
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleSelectConversation = async (userId: string) => {
    const conv = conversations.find((c) => c.userId === userId);
    const displayName =
      conv && conv.userName && conv.userName !== "Unknown"
        ? conv.userName
        : conv?.phone || "Customer";

    setSelectedUserId(userId);
    setSelectedUserName(displayName);
    setLoadingMessages(true);
    setMessages([]);

    try {
      const getMessages = httpsCallable(functions, "getMessagesForUser");
      const result = await getMessages({ userId, limit: 100 });
      const data = result.data as any;

      if (data.success) {
        const rawMessages = data.messages || [];

        const normalized: NormalizedMessage[] = rawMessages.map((m: any) => {
          // normalize timestamp to Date
          let ts: any =
            m.rawTimestamp ?? m.timestamp ?? m.timestampMs ?? m.createdAt ?? null;
          let date: Date | null = null;

          if (ts && typeof ts.toDate === "function") {
            date = ts.toDate();
          } else if (ts instanceof Date) {
            date = ts;
          } else if (typeof ts === "number") {
            date = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
          } else if (typeof ts === "string") {
            const num = Number(ts);
            date = !isNaN(num) ? new Date(num * 1000) : new Date(ts);
          }

          const content =
            typeof m.content === "string"
              ? m.content
              : m.text?.body
              ? String(m.text.body)
              : m.message
              ? String(m.message)
              : m.context?.body
              ? String(m.context.body)
              : `[${m.type || "event"}]`;

          return {
            id: m.id,
            content,
            text: m.text,
            message: m.message,
            type: m.type,
            from: m.from,
            direction:
              m.direction ??
              (m.from === "user" || m.from === "customer"
                ? "inbound"
                : "outbound"),
            timestamp: date,
            status: m.status || "",
          };
        });

        console.log(`✅ Loaded ${normalized.length} messages for ${userId}`);
        setMessages(normalized);
      } else {
        console.error("❌ Failed to load messages:", data.error);
        toast.error("Failed to load messages");
      }
    } catch (error: any) {
      console.error("❌ Error loading messages:", error);
      toast.error(error.message || "Failed to load conversation");
    } finally {
      setLoadingMessages(false);
    }
  };

  return (
    <div className="whatsapp-dashboard flex h-full min-h-[420px] w-full overflow-hidden bg-white">
      {/* Conversation list — full width on mobile until a chat is opened */}
      <div
        className={`flex flex-col border-r border-gray-200 bg-white ${
          selectedUserId ? "hidden md:flex md:w-[320px] lg:w-[350px]" : "flex w-full md:w-[320px] lg:w-[350px]"
        }`}
      >
        <div className="flex-shrink-0 bg-[#075E54] px-4 py-3 text-white">
          <h2 className="m-0 text-base sm:text-lg font-semibold">WhatsApp Messages</h2>
          <p className="m-0 mt-1 text-xs opacity-90">
            {conversations.length} conversations
          </p>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50">
          {loadingConversations ? (
            <div className="p-5 text-center text-sm text-gray-500">
              Loading conversations...
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">
              No conversations yet
            </div>
          ) : (
            <ConversationList
              conversations={conversations}
              selectedUserId={selectedUserId}
              onSelectConversation={handleSelectConversation}
            />
          )}
        </div>
      </div>

      {/* Thread — full screen on mobile when selected */}
      <div
        className={`min-w-0 flex-1 flex-col ${
          selectedUserId ? "flex" : "hidden md:flex"
        }`}
      >
        {selectedUserId ? (
          loadingMessages ? (
            <div className="flex flex-1 items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500" />
                <p className="text-sm text-gray-600">Loading messages...</p>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-3 py-2 md:hidden">
                <button
                  type="button"
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-[#075E54] active:bg-gray-100"
                  onClick={() => {
                    setSelectedUserId(null);
                    setSelectedUserName("");
                    setMessages([]);
                  }}
                >
                  ← Chats
                </button>
                <span className="truncate text-sm font-bold text-gray-800">
                  {selectedUserName || selectedUserId}
                </span>
              </div>
              <div className="min-h-0 flex-1">
                <MessageThread
                  userId={selectedUserId}
                  userName={selectedUserName || selectedUserId}
                  userPhone={selectedConv?.phone || ""}
                  messages={messages}
                  onRefresh={() => handleSelectConversation(selectedUserId)}
                />
              </div>
            </div>
          )
        ) : (
          <div className="hidden flex-1 items-center justify-center bg-gray-50 text-gray-400 md:flex">
            <div className="text-center px-4">
              <div className="mb-3 text-4xl">💬</div>
              <p className="m-0 text-base">Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
